'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@web-libs/features/auth/main/application/use-auth.concern';
import { useResponsive } from '@web-libs/core/responsiveness/use-responsive';
import { useOwnUser } from "@web-libs/features/users/application/use-own-user.concern";
import { useDictionaryContext } from "@web-libs/core/internationalization/dictionary.context";
import { useHttpContext } from "@web-libs/core/http/infrastructure/client-side/http.context";
import AngularWrapper from '@web-app/_main/angular-wrapper';
import { FloatingActionsContainerComponent } from '@tremaze/tagea-web-floating-actions';

type TimeTrackingProvider = "DISABLED" | "VIVENDI" | "FACTORIAL" | "TAGEA";

export default function FloatingActionsWrapper() {
  // Time tracking state and hooks
  const [timeTrackingProvider, setTimeTrackingProvider] = useState<TimeTrackingProvider | null>(null);
  const { isEmployee } = useOwnUser();
  const dict = useDictionaryContext();
  const http = useHttpContext();
  
  // Tagea AI state and hooks
  const { authToken, isAuthenticated } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const tenantId = http.tenantId || '';

  // Check time tracking availability
  useEffect(() => {
    const checkTimeTracking = async () => {
      try {
        const isEmp = await isEmployee();
        if (!isEmp) {
          setTimeTrackingProvider("DISABLED");
          return;
        }

        const provider = await http.get<TimeTrackingProvider>("/users/me/timeTracking/isEnabled");
        setTimeTrackingProvider(provider);
      } catch (error) {
        console.error("Error checking time tracking status:", error);
        setTimeTrackingProvider("DISABLED");
      }
    };

    checkTimeTracking();
  }, [isEmployee, http]);

  // Check Tagea AI feature availability
  useEffect(() => {
    // Check if feature is enabled - this is a simplified check
    // In a real implementation, you might want to check tenant configuration
    setFeatureEnabled(true);
  }, []);

  // Get the appropriate subtitle based on the provider
  const getSubtitle = () => {
    switch (timeTrackingProvider) {
      case "VIVENDI":
        return dict?.angular?.timeTracking?.timeTrackerSheet_Vivendi_Text;
      case "FACTORIAL":
        return dict?.angular?.timeTracking?.timeTrackerSheet_Factorial;
      case "TAGEA":
        return dict?.angular?.timeTracking?.timeTrackerSheet_Tagea;
      default:
        return dict?.angular?.timeTracking?.subtitle; // fallback
    }
  };

  // Create translation objects from dictionary
  const submitFormTranslations = {
    startLabel: dict?.angular?.timeTracking?.startLabel,
    endLabel: dict?.angular?.timeTracking?.endLabel,
    breakLabel: dict?.angular?.timeTracking?.breakLabel,
    commentLabel: dict?.angular?.timeTracking?.commentLabel,
    minTimeError: dict?.angular?.timeTracking?.minTimeError,
    endTimeError: dict?.angular?.timeTracking?.endTimeError
  };

  const liveTrackingTranslations = {
    title: dict?.angular?.timeTracking?.title,
    subtitle: getSubtitle(),
    cancelButton: dict?.angular?.timeTracking?.cancelButton,
    saveButton: dict?.angular?.timeTracking?.saveButton,
    deleteConfirmTitle: dict?.angular?.timeTracking?.deleteConfirmTitle,
    deleteConfirmText: dict?.angular?.timeTracking?.deleteConfirmText,
    savedNotification: dict?.angular?.timeTracking?.savedNotification,
    errorNotification: dict?.angular?.timeTracking?.errorNotification,
    submitFormTranslations
  };

  const historyTranslations = {
    title: dict?.angular?.timeTracking?.historyTitle,
    newTimeTrackingTitle: dict?.angular?.timeTracking?.newTimeTrackingTitle,
    timeRangeLabel: dict?.angular?.timeTracking?.timeRangeLabel,
    dateColumnLabel: dict?.angular?.timeTracking?.dateColumnLabel,
    startColumnLabel: dict?.angular?.timeTracking?.startColumnLabel,
    endColumnLabel: dict?.angular?.timeTracking?.endColumnLabel,
    breakColumnLabel: dict?.angular?.timeTracking?.breakColumnLabel,
    cancelButton: dict?.angular?.timeTracking?.cancelButton,
    saveButton: dict?.angular?.timeTracking?.saveButton,
    savedNotification: dict?.angular?.timeTracking?.savedNotification,
    errorNotification: dict?.angular?.timeTracking?.errorNotification,
    submitFormTranslations
  };

  // Determine what to show
  const showTimeTracking = timeTrackingProvider && timeTrackingProvider !== "DISABLED";
  const showTageaAi = isAuthenticated && featureEnabled;
  
  // Don't render anything if neither FAB should show
  if (!showTimeTracking && !showTageaAi) {
    return null;
  }

  return (
    <AngularWrapper
      component={FloatingActionsContainerComponent}
      inputs={{
        showTageaAi,
        showTimeTracking,
        tenantId,
        fullscreen: isMobile || isTablet,
        liveTrackingTranslations,
        historyTranslations
      }}
    />
  );
}